<script lang="ts" setup>
import type {
	NewSearchSuggestProps,
	Suggestion,
	SuggestionCondoResult,
	SuggestionGroupKey
} from '.';
import type { SuggestionGroup } from '~/composables/useLocationAutocomplete';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';

import {
	Command,
	CommandDialog,
	CommandList,
	CommandSeparator
} from '@/components/SearchSuggestRadixCommand';
import { cn } from '@/utils';
import { useNewSearchSuggest } from '~/composables/useNewSearchSuggest';
import { useSingleFieldSuggest } from '~/composables/useSingleFieldSuggest';
import { SEARCH_SUGGEST } from '~/constants';

import IconMaterialSymbolsLightArrowLeftAlt from '~icons/material-symbols-light/arrow-left-alt';
import IconMaterialSymbolsLightCancel from '~icons/material-symbols-light/cancel';
import HistoryGroupIcon from '~icons/material-symbols-light/history';
import IconMaterialSymbolsLightSearch from '~icons/material-symbols-light/search';

import { SEARCH_PLACEHOLDERS } from '.';
import ListingEmptyState from '../ListingEmptyState/EmptyState.vue';
import CodeGroup from './CodeGroup.vue';
import LoadingGroup from './LoadingGroup.vue';
import NewGroup from './NewGroup.vue';
import NewSuggestInput from './NewSuggestInput.vue';

const props = withDefaults(
	defineProps<NewSearchSuggestProps & { placeholderClasses?: string }>(),
	{
		isFetchingPropertyByCodeSearch: false,
		placeholders: () => [...SEARCH_PLACEHOLDERS.animated],
		showInputButton: true
	}
);

const emit = defineEmits<{
	(e: 'select-address', value: SelectAddressEvent): void;
	(e: 'select-place', value: Suggestion['searchParams']): void;
	(e: 'select-condo', value: SuggestionCondoResult): void;
	(e: 'select-code', value: Suggestion['searchParams']): void;
	(e: 'update:search-term', value: string): void;
	(e: 'emptyResultOnFilter', searchTerm: string): void;
	(e: 'clear'): void;
	(e: 'close-modal'): void;
}>();

const isOpen = defineModel<boolean>('isOpen', { required: true });
const selectedValue = ref<SuggestionGroup>();

// Placeholder sempre usa o dialog mode
const effectivePlaceholders = computed(() => {
	return [SEARCH_PLACEHOLDERS.dialog];
});

const normalizedPlaceholder = computed<string | undefined>(() => {
	const placeholders = props.placeholders;
	if (typeof placeholders === 'string') {
		return placeholders;
	}
	if (Array.isArray(placeholders)) {
		return placeholders[0];
	}
	return undefined;
});

const { selectedMacroregion: globalSelectedMacroregion } =
	useSingleFieldSuggest();

const {
	// State
	searchTerm,
	modelValue,
	commandEl,
	isUpdatingFromWatch,

	// Computed
	hasMinSearchTerm,
	searchByCodeOption,
	shouldShowCodeGroup,
	shouldShowEmptyState,
	shouldShowLastResearchs,
	locationAutocompleteData,
	loading,
	dynamicPlaceholder,
	placeholders: computedPlaceholders,

	// Research suggestions
	filteredLastResearchs,
	removeResearchs,

	// Functions
	clear,
	getDisplayValue,
	handleKeyDown,
	handleModelValueUpdate: baseHandleModelValueUpdate,
	executeLocationAutocomplete,
	handleApplySearchTerm
} = useNewSearchSuggest({
	props,
	emit,
	isDropdownOpen: isOpen,
	macroregion: globalSelectedMacroregion
});

let savedScrollPosition = 0;
let originalHtmlOverflow = '';

function preventBodyScroll() {
	savedScrollPosition = window.scrollY;
	originalHtmlOverflow = document.documentElement.style.overflow;
	document.documentElement.style.overflow = 'hidden';
	document.body.classList.add('scroll-locked');
	document.body.style.top = `-${savedScrollPosition}px`;
}

function restoreBodyScroll() {
	document.documentElement.style.overflow = originalHtmlOverflow;

	document.body.classList.remove('scroll-locked');
	document.body.style.removeProperty('position');
	document.body.style.removeProperty('top');
	document.body.style.removeProperty('width');

	document.body.style.overflow = '';
	document.body.style.height = '';
	document.body.style.touchAction = '';

	requestAnimationFrame(() => {
		window.scrollTo(0, savedScrollPosition);
	});
}

function openDialog() {
	// Defer heavy modal rendering to next frame to reduce input delay (INP optimization)
	requestAnimationFrame(() => {
		isOpen.value = true;
		clear(false);

		if (searchTerm.value.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH) {
			executeLocationAutocomplete();
		}
	});
}

function closeDialog() {
	if (!modelValue.value && props.searchTerm) {
		handleApplySearchTerm();
	}
	isOpen.value = false;

	if (!searchTerm.value && !modelValue.value) {
		validateAndStartAnimation();
	}
}

function handleModelValueUpdate(suggestion: Suggestion) {
	baseHandleModelValueUpdate(suggestion, true);

	// Limpar o searchTerm após seleção
	nextTick(() => {
		searchTerm.value = '';
	});

	closeDialog();
}

async function handleInputClear() {
	clear();
	isOpen.value = true;

	if (searchTerm.value.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH) {
		executeLocationAutocomplete();
	}

	await nextTick();
}

watch(
	() => props.searchTerm,
	value => {
		if (value && value.trim()) {
			handleApplySearchTerm();
		} else if (value === undefined || value === null) {
			isUpdatingFromWatch.value = true;
			searchTerm.value = '';
		}
	},
	{ immediate: true }
);

watch(
	() => props.isFetchingPropertyByCodeSearch,
	isFetching => {
		if (!isFetching) closeDialog();
	}
);

const animatedPlaceholder = shallowRef<string>('');
const placeholderIndex = shallowRef<number>(0);
const isDeleting = shallowRef<boolean>(true);
const isPaused = shallowRef<boolean>(false);
const lastUpdateTime = shallowRef<number>(0);
const isFirstRender = shallowRef<boolean>(true);

const placeholderCount = computed<number>(() =>
	Array.isArray(effectivePlaceholders.value)
		? effectivePlaceholders.value?.length || 0
		: 0
);

const currentPlaceholder = computed<string>(() => {
	const index = placeholderIndex.value % placeholderCount.value;
	return effectivePlaceholders.value[index] || '';
});

const computedPlaceholder = computed<string | undefined>(() => {
	// Se tiver placeholders customizados via props, usar eles
	if (normalizedPlaceholder.value) {
		return normalizedPlaceholder.value;
	}

	// Caso contrário, usar a lógica do composable (SRP com placeholder dinâmico)
	if (import.meta.server) {
		return Array.isArray(computedPlaceholders.value)
			? computedPlaceholders.value?.[0] || ''
			: computedPlaceholders.value;
	}

	if (modelValue.value?.searchType === 'region') {
		return modelValue.value?.result.subtitle || '';
	}

	return Array.isArray(computedPlaceholders.value)
		? animatedPlaceholder.value
		: computedPlaceholders.value;
});

const selectedDisplayText = computed<string>(() => {
	if (dynamicPlaceholder.value) {
		return dynamicPlaceholder.value;
	}

	if (modelValue.value) {
		return getDisplayValue(modelValue.value);
	}
	return searchTerm.value || computedPlaceholder.value || '';
});

const { pause, resume } = useRafFn(
	() => {
		if (!placeholderCount.value || isPaused.value) return;

		const now = performance.now();
		const elapsed = now - lastUpdateTime.value;

		const delay = calculateDelay();

		if (elapsed >= delay) {
			updatePlaceholderText();
			lastUpdateTime.value = now;
		}
	},
	{ immediate: false }
);

const updatePlaceholderText = useThrottleFn(async () => {
	if (isDeleting.value) {
		animatedPlaceholder.value = currentPlaceholder.value.substring(
			0,
			animatedPlaceholder.value.length - 1
		);

		if (animatedPlaceholder.value === '') {
			isPaused.value = true;
			const timeout = setTimeout(() => {
				isPaused.value = false;
				isDeleting.value = false;
				placeholderIndex.value =
					(placeholderIndex.value + 1) % placeholderCount.value;
				clearTimeout(timeout);
			}, 500);
		}
	} else {
		if (animatedPlaceholder.value === '') {
			animatedPlaceholder.value = currentPlaceholder.value.charAt(0);
		} else if (
			animatedPlaceholder.value.length < currentPlaceholder.value.length
		) {
			animatedPlaceholder.value = currentPlaceholder.value.substring(
				0,
				animatedPlaceholder.value.length + 1
			);

			if (animatedPlaceholder.value === currentPlaceholder.value) {
				isPaused.value = true;
				setTimeout(() => {
					isPaused.value = false;
					isDeleting.value = true;
				}, 1500);
			}
		}
	}

	isFirstRender.value = false;
}, 16);

function calculateDelay(): number {
	let delay = 75;

	if (isDeleting.value) {
		delay /= 2;
	}

	return delay;
}

function startAnimation() {
	animatedPlaceholder.value = isFirstRender.value
		? currentPlaceholder.value
		: '';
	isDeleting.value = isFirstRender.value ? true : false;
	placeholderIndex.value = 0;
	isPaused.value = false;
	lastUpdateTime.value = performance.now();
	resume();
}

function validateAndStartAnimation() {
	if (placeholderCount.value) {
		startAnimation();
	}
}

onMounted(() => {
	validateAndStartAnimation();
});

onBeforeUnmount(() => {
	pause();
	if (isOpen.value) {
		restoreBodyScroll();
	}
});

const inputEl = useTemplateRef<InstanceType<typeof NewSuggestInput>>('inputEl');

function focusInput() {
	inputEl.value?.focus();
}

watch(isOpen, newValue => {
	if (newValue) {
		preventBodyScroll();
	} else {
		restoreBodyScroll();
	}
});
</script>
<template>
	<div class="relative h-full max-w-full overflow-hidden">
		<div
			v-if="showInputButton"
			:class="cn('flex size-full items-center', inputWrapperClasses)"
		>
			<slot name="input-prefix" />
			<button
				:class="
					cn(
						'z-10 flex h-11 flex-1 min-w-0 items-center text-ellipsis rounded-md bg-white text-primary',
						buttonInputClasses
					)
				"
				@click="openDialog"
			>
				<span
					:class="cn('truncate text-left text-black/50', placeholderClasses)"
				>
					{{ selectedDisplayText }}
				</span>
			</button>
			<div class="ml-auto flex items-center">
				<slot name="suffix">
					<div
						:class="
							cn(
								'flex h-full w-7 items-center justify-end',
								inputClearIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightCancel
							v-show="searchTerm"
							:class="
								cn(
									'size-5 min-w-5 cursor-pointer text-white-dark-3',
									inputClearIconClasses
								)
							"
							@click.stop="handleInputClear"
						/>
					</div>
					<div
						:class="
							cn(
								'grid h-full w-[62px] place-items-center',
								inputSearchIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightSearch
							:class="cn('size-7 text-primary', inputSearchIconClasses)"
						/>
					</div>
				</slot>
			</div>
		</div>

		<Transition name="slide-up">
			<div v-if="isOpen" class="absolute left-0 top-0 h-dvh w-dvw max-w-none">
				<CommandDialog
					:open="isOpen"
					class="h-dvh w-dvw max-w-none"
					:show-close-icon="false"
					title="Inicie sua busca"
					hide-title
					@update:open="isOpen = $event"
				>
					<div ref="commandEl" class="h-dvh w-dvw bg-white">
						<Command
							v-model="modelValue"
							v-model:search-term="searchTerm"
							v-model:selected-value="selectedValue"
							:display-value="getDisplayValue"
							reset-search-term-on-blur
							reset-search-term-on-select
							class="h-dvh w-full bg-white [&_[data-highlighted]:hover]:bg-white-dark-2 [&_[data-highlighted]:hover]:text-primary **:data-highlighted:bg-transparent **:data-highlighted:text-inherit"
							@update:model-value="
								event => handleModelValueUpdate(event as Suggestion)
							"
							@keydown="handleKeyDown"
						>
							<NewSuggestInput
								ref="inputEl"
								:placeholder="SEARCH_PLACEHOLDERS.dialog"
								:enable-animation="false"
								:class="
									cn(
										'h-[62px] text-ellipsis pl-5 text-primary/40 placeholder:text-primary/40 focus:placeholder:opacity-100 ',
										'appearance-none transform-gpu relative',
										'scroll-mt-20 scroll-mb-5',
										'ios-safe-input',
										inputClasses
									)
								"
								:model-value="searchTerm"
								@clear="handleInputClear"
							>
								<template #prefix>
									<div class="ml-5 flex size-6 items-center justify-center">
										<IconMaterialSymbolsLightArrowLeftAlt
											class="size-full h-6! w-6! text-primary"
											@click="closeDialog"
										/>
									</div>
								</template>
							</NewSuggestInput>
							<CommandSeparator class="bg-white-dark-2" />

							<CommandList
								:class="
									cn(
										'h-full max-h-none border border-t-0 border-white-dark-2 bg-white p-0 transition-all duration-300 ease-out',
										loading ? 'overflow-hidden!' : 'overflow-y-auto'
									)
								"
								data-scroll-area
							>
								<div v-show="shouldShowEmptyState" class="px-5 py-6">
									<ListingEmptyState
										:show-badges="false"
										icon-wrapper-classes="size-[47.5px]"
										icon-classes="size-[22.5px]"
										title-classes="text-lg"
										description-classes="text-sm"
									>
										<template #title>Nenhum resultado encontrado</template>
										<template #description>
											Tente uma busca diferente ou verifique a grafia
										</template>
									</ListingEmptyState>
								</div>

								<CommandSeparator
									v-if="shouldShowLastResearchs && hasMinSearchTerm"
									class="bg-white-dark-2"
								/>

								<NewGroup
									v-if="shouldShowLastResearchs"
									:suggestion-key="'lastResearchs'"
									:heading="{
										text: 'Últimas buscas',
										icon: HistoryGroupIcon
									}"
									:suggestions="filteredLastResearchs"
									:search-term="searchTerm"
									is-removable
									@remove-suggestion="removeResearchs"
								/>

								<TransitionGroup name="fade" tag="div">
									<LoadingGroup
										v-for="index in 6"
										:key="`loading-group-${index}`"
										:active="loading"
									/>
								</TransitionGroup>

								<template v-if="!loading">
									<template
										v-for="(suggestionGroup, index) in locationAutocompleteData"
										:key="`suggestion-group-${suggestionGroup.key}`"
									>
										<CommandSeparator
											class="bg-white-dark-2"
											:class="{
												hidden: !suggestionGroup.visible || index === 0
											}"
										/>

										<NewGroup
											:suggestion-key="
												suggestionGroup.key as SuggestionGroupKey
											"
											:heading="suggestionGroup.heading"
											:suggestions="suggestionGroup.suggestions"
											:search-term="searchTerm"
										/>
									</template>

									<CodeGroup
										v-if="shouldShowCodeGroup"
										:search-term="searchTerm"
										suggestion-key="code"
										:suggestions="searchByCodeOption"
										:is-fetching-property-by-code-search="
											isFetchingPropertyByCodeSearch
										"
									/>
								</template>
							</CommandList>
						</Command>
					</div>
				</CommandDialog>
			</div>
		</Transition>
	</div>
</template>
<!-- Styles moved to app/assets/css/main.css to avoid render-blocking CSS -->
